import { useEffect, useMemo, useState } from 'react'
import { formatMoney } from '../lib/format.js'

// Owner attestation modal. Surfaces every unconfirmed savings event
// with its methodology and lets the owner act on each row individually:
//
//   ✓  Confirm  — keep the event at its computed amount, mark verified
//   ✎  Edit     — adjust the dollar amount inline, then verify
//   ✗  Dismiss  — zero this event out (kept for audit, removed from total)
//
// The headline tile recalculates live as the owner makes choices, and
// the bottom button shows the running total so the owner sees what
// they're committing to before confirming. See Instructions Claude.md §7e.
//
// Why per-row matters: a flat "confirm everything" forces owners who
// don't believe a row to walk away entirely. They lose trust in the
// whole tile because they can't disagree with one entry. Per-row
// review lets honest disagreements get reflected in the data without
// breaking the credibility of the rest.
export default function OwnerAttestationModal({
  open,
  onClose,
  onConfirmed, // (decisions) => Promise — caller applies the per-row decisions
  events,
}) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState(null)

  // Per-row decisions. Default for each event: { action:'confirm', amount: original }.
  // Keyed by event id.
  const [decisions, setDecisions] = useState({})

  // Whenever the modal opens with a new events array, hydrate decisions.
  useEffect(() => {
    if (!open) return
    const next = {}
    for (const e of events ?? []) {
      next[e.id] = { action: 'confirm', amount: Number(e.dollar_amount || 0) }
    }
    setDecisions(next)
    setError(null)
  }, [open, events])

  // Helpers to mutate a single row's decision.
  const setRow = (id, patch) =>
    setDecisions((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }))

  const startEdit = (e) => setRow(e.id, { action: 'edit' })
  const cancelEdit = (e) =>
    setRow(e.id, { action: 'confirm', amount: Number(e.dollar_amount || 0) })
  const saveEdit = (e) => {
    const cur = decisions[e.id]
    const num = Number(cur?.amount)
    if (!Number.isFinite(num) || num < 0) return // ignore bad input
    setRow(e.id, { action: 'confirm', amount: num })
  }
  const dismiss = (id) => setRow(id, { action: 'dismiss' })
  const restore = (e) =>
    setRow(e.id, { action: 'confirm', amount: Number(e.dollar_amount || 0) })

  // Live tallies derived from current decisions.
  const tally = useMemo(() => {
    let total = 0
    let included = 0
    let dismissed = 0
    for (const e of events ?? []) {
      const d = decisions[e.id]
      if (!d || d.action === 'dismiss') {
        dismissed += d?.action === 'dismiss' ? 1 : 0
        continue
      }
      total += Number(d.amount || 0)
      included += 1
    }
    return { total, included, dismissed }
  }, [events, decisions])

  if (!open) return null

  const count = events?.length ?? 0

  // Friendly source label for the methodology breakdown.
  const sourceLabel = (s) => {
    switch (s) {
      case 'daily_under_target':  return 'Daily under target'
      case 'waste_avoided':       return 'Waste avoided'
      case 'reorder_reduction':   return 'Reorder reduction'
      case 'par_optimization':    return 'Par optimization'
      case 'supplier_swap':       return 'Supplier swap'
      case 'recipe_costing':      return 'Recipe costing'
      case 'menu_engineering':    return 'Menu engineering'
      case 'labor_optimization':  return 'Labor optimization'
      case 'theft_detection':     return 'Theft detection'
      case 'marketing_lift':      return 'Marketing lift'
      case 'manual_attestation':  return 'Manual attestation'
      default:                    return s
    }
  }

  const fmtDate = (iso) => {
    if (!iso) return ''
    const d = new Date(iso)
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  }

  async function handleConfirm() {
    if (submitting || count === 0) return
    setSubmitting(true)
    setError(null)
    try {
      await onConfirmed?.(decisions)
      onClose?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[OwnerAttestationModal] confirm failed', e)
      setError('Could not save. Check your connection and try again.')
    } finally {
      setSubmitting(false)
    }
  }

  const buttonLabel = (() => {
    if (submitting) return 'Saving…'
    if (count === 0) return 'Nothing to confirm'
    if (tally.included === 0) return 'Confirm — all dismissed'
    if (tally.dismissed === 0 && tally.included === count) {
      // Nothing changed — preserve the friendlier original wording.
      return `Confirm ${formatMoney(tally.total)} — matches my records`
    }
    return `Confirm ${formatMoney(tally.total)} across ${tally.included} ${
      tally.included === 1 ? 'event' : 'events'
    }`
  })()

  return (
    <div className="fixed inset-0 z-40">
      {/* Backdrop */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/50 animate-fade-in"
      />

      {/* Sheet */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Review savings"
        className="animate-sheet-up absolute inset-x-0 bottom-0 max-h-[92vh] overflow-y-auto rounded-t-2xl bg-white shadow-2xl"
      >
        {/* Sticky header */}
        <div className="sticky top-0 z-10 bg-white pt-2">
          <div className="mx-auto h-1.5 w-12 rounded-full bg-black/15" />
          <div className="flex items-center justify-between px-4 py-3">
            <h2 className="text-xl font-medium">Review savings</h2>
            <button
              type="button"
              onClick={onClose}
              className="min-h-btn min-w-[48px] rounded-lg px-3 text-base font-medium text-black/70 hover:bg-black/5"
              aria-label="Cancel"
            >
              Cancel
            </button>
          </div>
        </div>

        <div className="space-y-5 px-4 pb-4">
          {/* Headline — recalculates live as owner edits/dismisses rows */}
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
            <div className="text-xs font-medium text-emerald-700">
              Verified total
            </div>
            <div className="mt-1 text-3xl font-medium tabular-nums text-emerald-700">
              {formatMoney(tally.total)}
            </div>
            <div className="mt-1 text-xs text-emerald-900/70">
              {tally.included} of {count} {count === 1 ? 'event' : 'events'} included
              {tally.dismissed > 0 ? ` · ${tally.dismissed} dismissed` : ''}
            </div>
          </div>

          {count === 0 ? (
            <div className="rounded-xl border border-dashed border-black/15 px-4 py-8 text-center text-sm text-black/60">
              Nothing to review right now. New events will appear here
              as the kitchen logs waste over the coming days.
            </div>
          ) : (
            <>
              <div>
                <div className="mb-2 text-base font-medium">
                  What we tracked
                </div>
                <ul className="overflow-hidden rounded-xl border border-black/10">
                  {events.map((e) => {
                    const d = decisions[e.id] ?? { action: 'confirm', amount: Number(e.dollar_amount || 0) }
                    const isDismissed = d.action === 'dismiss'
                    const isEditing   = d.action === 'edit'

                    return (
                      <li
                        key={e.id}
                        className={`border-b border-black/5 px-4 py-3 last:border-b-0 ${
                          isDismissed ? 'bg-black/5 opacity-60' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 text-xs text-black/60">
                              <span>{fmtDate(e.occurred_at)}</span>
                              <span>·</span>
                              <span className={`truncate ${isDismissed ? 'line-through' : ''}`}>
                                {sourceLabel(e.source)}
                              </span>
                            </div>
                            <div className="mt-0.5 text-xs text-black/55 leading-snug">
                              {e.calculation_method}
                            </div>
                          </div>

                          {/* Right side: amount + per-row actions */}
                          <div className="flex shrink-0 flex-col items-end gap-1.5">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <span className="text-sm text-black/55">$</span>
                                <input
                                  type="number"
                                  inputMode="decimal"
                                  step="0.01"
                                  min="0"
                                  autoFocus
                                  value={d.amount}
                                  onChange={(ev) => setRow(e.id, { amount: ev.target.value })}
                                  onKeyDown={(ev) => {
                                    if (ev.key === 'Enter') saveEdit(e)
                                    if (ev.key === 'Escape') cancelEdit(e)
                                  }}
                                  className="w-24 rounded-md border border-emerald-300 bg-white px-2 py-1 text-right text-base font-medium tabular-nums text-emerald-700 focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                                  aria-label="Edit dollar amount"
                                />
                              </div>
                            ) : (
                              <div className={`text-base font-medium tabular-nums ${
                                isDismissed ? 'text-black/40 line-through' : 'text-emerald-700'
                              }`}>
                                {formatMoney(isDismissed ? 0 : Number(d.amount || 0))}
                              </div>
                            )}

                            <div className="flex items-center gap-1">
                              {isDismissed ? (
                                <button
                                  type="button"
                                  onClick={() => restore(e)}
                                  className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50"
                                >
                                  ↺ Restore
                                </button>
                              ) : isEditing ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => saveEdit(e)}
                                    className="rounded-md bg-emerald-600 px-2 py-1 text-xs font-medium text-white hover:bg-emerald-700"
                                    aria-label="Save edit"
                                  >
                                    Save
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => cancelEdit(e)}
                                    className="rounded-md px-2 py-1 text-xs font-medium text-black/60 hover:bg-black/5"
                                    aria-label="Cancel edit"
                                  >
                                    Cancel
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => startEdit(e)}
                                    className="rounded-md px-2 py-1 text-xs font-medium text-black/65 hover:bg-black/10"
                                    aria-label="Edit dollar amount"
                                    title="Edit"
                                  >
                                    ✎ Edit
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => dismiss(e.id)}
                                    className="rounded-md px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50"
                                    aria-label="Dismiss this event"
                                    title="Dismiss"
                                  >
                                    ✗ Dismiss
                                  </button>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    )
                  })}
                </ul>
              </div>

              {/* Plain-English ask */}
              <div className="rounded-xl bg-black/5 p-4 text-sm text-black/80">
                Confirm what matches your books. Edit any amount that's
                off, dismiss what doesn't belong. The Verified total
                above is the number we'll show as the headline going
                forward.
              </div>
            </>
          )}

          {error && (
            <div className="rounded-lg bg-red-600 px-3 py-2 text-sm text-white">
              {error}
            </div>
          )}
        </div>

        {/* Sticky confirm */}
        <div className="sticky bottom-0 z-10 border-t border-black/5 bg-white px-4 pt-3 safe-bottom">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting || count === 0}
            className="min-h-btn w-full rounded-lg bg-emerald-600 px-4 py-4 text-xl font-medium text-white shadow-sm hover:bg-emerald-700 active:bg-emerald-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {buttonLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
